import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { environment } from '../../environments/environment';
import { readStoredLanguage } from '../core/helpers/language-storage.helper';
import {
  computeClockOffsetMs,
  deviceTimezone,
  parseSystemTimestamp,
  parseSystemUserTimezone,
} from '../core/helpers/server-clock.helper';
import { apiList } from './apiList.service';

@Injectable({
  providedIn: 'root',
})
export class ServerClockService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly apis = inject(apiList);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private readonly _offsetMs = signal(0);
  readonly offsetMs = this._offsetMs.asReadonly();

  private readonly _userTimezone = signal('');
  readonly userTimezone = this._userTimezone.asReadonly();

  private inFlight: Promise<void> | null = null;

  nowMs(): number {
    return Date.now() + this._offsetMs();
  }

  timezone(): string {
    return this._userTimezone() || deviceTimezone();
  }

  sync(): Promise<void> {
    if (!this.isBrowser) {
      return Promise.resolve();
    }

    if (this.inFlight) {
      return this.inFlight;
    }

    this.inFlight = this.fetchOffset().finally(() => {
      this.inFlight = null;
    });

    return this.inFlight;
  }

  private async fetchOffset(): Promise<void> {
    const localStart = Date.now();

    try {
      const response = await fetch(this.buildUrl(), {
        method: 'GET',
        cache: 'no-store',
        signal: AbortSignal.timeout(5000),
        headers: {
          Accept: 'application/json',
          'Accept-Language': readStoredLanguage(),
          'x-device-type': 'WEB',
          'x-portal': 'SERVICE_PROVIDER',
          'x-timezone': this.timezone(),
        },
      });

      const localEnd = Date.now();

      if (!response.ok) {
        return;
      }

      const payload: unknown = await response.json();
      const serverTimestamp = parseSystemTimestamp(payload);
      const userTimezone = parseSystemUserTimezone(payload);

      if (userTimezone) {
        this._userTimezone.set(userTimezone);
      }

      if (serverTimestamp === null) {
        return;
      }

      this._offsetMs.set(
        computeClockOffsetMs(serverTimestamp, localEnd, localStart)
      );
    } catch {
      // Fail open — keep the last known offset (0 if never synced).
    }
  }

  private buildUrl(): string {
    return `${environment.base_url}${this.apis.system}`;
  }
}
